import { Accordion as ArkAccordion } from "@ark-ui/react";
import type { ReactNode } from "react";
import { createContext, useContext } from "react";

import chevronRightIcon from "@/assets/icons/chevron-right.svg";
import { twResolve } from "@/helpers/tw-resolve";

type AccordionVariant = "default" | "compact" | "highlighted";

const AccordionVariantContext = createContext<AccordionVariant>("default");

type AccordionRootProps = {
  children: ReactNode;
  multiple?: boolean;
  collapsible?: boolean;
  defaultValue?: string[];
  value?: string[];
  onValueChange?: (details: { value: string[] }) => void;
  variant?: AccordionVariant;
};

function AccordionRoot({
  children,
  multiple = true,
  collapsible = true,
  variant = "default",
  ...props
}: AccordionRootProps): ReactNode {
  return (
    <AccordionVariantContext value={variant}>
      <ArkAccordion.Root multiple={multiple} collapsible={collapsible} className="w-full" {...props}>
        {children}
      </ArkAccordion.Root>
    </AccordionVariantContext>
  );
}

type AccordionItemProps = {
  children: ReactNode;
  value: string;
  disabled?: boolean;
  "data-testid"?: string;
};

function AccordionItem({ children, ...props }: AccordionItemProps): ReactNode {
  const variant = useContext(AccordionVariantContext);

  return (
    <ArkAccordion.Item
      className={twResolve(
        variant === "default" && "mb-space-sm last:mb-0",
        variant === "highlighted" && "overflow-hidden rounded-lg border border-border-brand bg-background-brand-light",
      )}
      {...props}
    >
      {children}
    </ArkAccordion.Item>
  );
}

type AccordionTriggerProps = {
  isNested?: boolean;
  children: ReactNode;
};

function AccordionTrigger({ children, isNested }: AccordionTriggerProps): ReactNode {
  const variant = useContext(AccordionVariantContext);

  return (
    <ArkAccordion.ItemTrigger
      className={twResolve(
        "group flex w-full items-center justify-start gap-space-sm overflow-hidden rounded-xs border-2 border-transparent p-space-sm text-left transition-colors hover:bg-background-secondary focus:outline-hidden focus-visible:bg-background-secondary data-[state=open]:bg-background-secondary data-[state=open]:text-text-brand",
        variant === "compact" && "border-0",
        variant === "highlighted" &&
          "rounded-none border-0 hover:bg-background-brand-tertiary-light data-[state=open]:bg-background-brand-tertiary-light",
      )}
    >
      <span className="shrink-0 fill-transparent transition-transform group-data-[state=open]:rotate-90">
        <svg width={16} height={16} aria-hidden="true">
          <use xlinkHref={`#${chevronRightIcon}`} />
        </svg>
      </span>
      <span className={isNested ? "text-body" : "text-body-emphasis"}>{children}</span>
    </ArkAccordion.ItemTrigger>
  );
}

function AccordionContent({ children }: { children: ReactNode }): ReactNode {
  const variant = useContext(AccordionVariantContext);

  return (
    <ArkAccordion.ItemContent
      className={twResolve(
        "bg-background-secondary p-space-md pt-0",
        variant === "compact" && "px-space-sm",
        variant === "highlighted" && "bg-transparent",
      )}
    >
      {children}
    </ArkAccordion.ItemContent>
  );
}

export const Accordion = {
  Root: AccordionRoot,
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent,
};
